/**
 * Ящик работы плана агроцикла: что за работа, её окно и ход, а менеджеру ещё и
 * правка (подпись, обязательность, окно датами), «Убрать из плана» и — для
 * запущенной — «Перенести» (срок такой работы меняет решение кампании).
 *
 * Сохраняет сам (`PATCH …/works/{id}`) и сообщает экрану новый цикл; хоким и
 * наблюдатель видят только факты и ссылку на работу.
 */

import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { DetailDrawer, DrawerFormActions } from '../../ui/DetailDrawer';
import { Badge } from '../../ui/Badge';
import { COMMON } from '../../../i18n/common';
import { defineMessages } from '../../../i18n/define';
import { useT } from '../../../i18n/hooks';
import { ApiError } from '../../../api/client';
import { agrocycleApi, type AgrocycleDetail, type PlanWork } from './agrocycleApi';
import { DateRangePicker, TextField, type DateRange } from '../../ui/forms';
import { RequiredToggle } from './PlanFields';
import { canReschedule, isLaunched } from './planEdit';

const m = defineMessages({
  failed: { id: 'cycle.plan.drawer.failed', defaultMessage: 'Не получилось — повторите.', description: 'Ошибка в ящике работы плана агроцикла (менеджер), когда сервер не дал своего текста' },
  factStage: { id: 'cycle.plan.drawer.stage', defaultMessage: 'Этап', description: 'Подпись факта в ящике работы плана: этап агроцикла' },
  factWindow: { id: 'cycle.plan.drawer.window', defaultMessage: 'Окно', description: 'Подпись факта в ящике работы плана: окно выполнения (даты)' },
  factState: { id: 'cycle.plan.drawer.state', defaultMessage: 'Состояние', description: 'Подпись факта в ящике работы плана: состояние работы' },
  factProgress: { id: 'cycle.plan.drawer.progress', defaultMessage: 'Ход', description: 'Подпись факта в ящике работы плана: сколько выполнено' },
  openWork: { id: 'cycle.plan.drawer.open', defaultMessage: 'Открыть работу', description: 'Ссылка в ящике работы плана: открыть страницу работы (кампании)' },
  launchedNote: {
    id: 'cycle.plan.drawer.launchedNote',
    defaultMessage: 'По работе идёт кампания: срок переносится решением кампании, и вместе с ним сдвигаются работы на полях без отчёта.',
    description: 'Пояснение в ящике запущенной работы плана (менеджер): почему окно правится не здесь',
  },
  reschedule: { id: 'cycle.plan.drawer.reschedule', defaultMessage: 'Перенести срок…', description: 'Кнопка в ящике запущенной работы плана (менеджер): открыть окно переноса кампании' },
  relativeNote: {
    id: 'cycle.plan.drawer.relativeNote',
    defaultMessage: 'Срок отсчитывается от другой работы: двигайте полосу на шкале — изменится смещение.',
    description: 'Пояснение в ящике работы плана (менеджер): у работы нет своих дат, она отсчитывается от другой',
  },
  labelField: { id: 'cycle.plan.drawer.label', defaultMessage: 'Подпись', description: 'Подпись поля: название работы плана, как его видят на экране (менеджер)' },
  windowField: { id: 'cycle.plan.drawer.windowField', defaultMessage: 'Окно работы', description: 'Подпись поля периода: окно работы плана (менеджер); одно поле «с — по»' },
  noChanges: { id: 'cycle.plan.drawer.noChanges', defaultMessage: 'Правок пока нет.', description: 'Причина выключенной кнопки «Сохранить» в ящике работы плана: ничего не изменено' },
  needLabel: { id: 'cycle.plan.drawer.needLabel', defaultMessage: 'Подпись не должна быть пустой.', description: 'Причина выключенной кнопки «Сохранить» в ящике работы плана: подпись пуста' },
  save: { id: 'cycle.plan.drawer.save', defaultMessage: 'Сохранить', description: 'Кнопка в ящике работы плана (менеджер): сохранить правку' },
  remove: { id: 'cycle.plan.drawer.remove', defaultMessage: 'Убрать из плана…', description: 'Кнопка в ящике работы плана (менеджер): открыть окно «убрать работу из плана»' },
  reasonSave: { id: 'cycle.plan.reason.edit', defaultMessage: 'правка работы плана', description: 'Причина правки плана, которую приложение отправляет серверу; потом видна в журнале плана' },
  saved: { id: 'cycle.plan.drawer.saved', defaultMessage: 'Сохранено: {name}', description: 'Уведомление после правки работы плана; {name} — название работы' },
});

type Params = Record<string, string | number | boolean | null | undefined>;

export interface WorkFacts {
  stage: string;
  window: string;
  state: string;
  progress: string | null;
  /** Страница работы (кампании); `null` — кампании ещё нет. */
  href: string | null;
  tags: string[];
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt>{label}</dt>
      <dd>{children}</dd>
    </>
  );
}

export function PlanWorkDrawer({
  cycle,
  work,
  facts,
  canEdit,
  params,
  onClose,
  onSaved,
  onReschedule,
  onRemove,
}: {
  cycle: AgrocycleDetail;
  work: PlanWork;
  facts: WorkFacts;
  canEdit: boolean;
  params: Params;
  onClose: () => void;
  /** Правка сохранена: новый цикл с сервера и уведомление. */
  onSaved: (cycle: AgrocycleDetail, message: string) => void;
  onReschedule: () => void;
  onRemove: () => void;
}) {
  const t = useT();
  const editable = canEdit && !isLaunched(work) && work.state === 'ahead';
  const byDates = work.timing.start_kind === 'date';
  const [label, setLabel] = useState(work.label);
  const [required, setRequired] = useState(work.is_required);
  // Конец окна у однодневной работы пуст: в поле периода это тот же день.
  const [range, setRange] = useState<DateRange>({ from: work.window_from ?? null, to: work.window_to ?? work.window_from ?? null });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const datesChanged = byDates && (range.from !== (work.window_from ?? null) || range.to !== (work.window_to ?? work.window_from ?? null));
  const changed = label.trim() !== work.label || required !== work.is_required || datesChanged;
  const min = `${cycle.anchor_year - 2}-01-01`;
  const max = `${cycle.anchor_year + 2}-12-31`;

  const save = async () => {
    if (busy || !changed || !label.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const result = await agrocycleApi.patchWork(
        cycle.key,
        work.id,
        {
          ...(label.trim() !== work.label ? { label: label.trim() } : {}),
          ...(required !== work.is_required ? { is_required: required } : {}),
          ...(datesChanged && range.from ? { window_from: range.from, window_to: range.to === range.from && !work.window_to ? null : range.to, approximate: work.approximate } : {}),
          reason: t(m.reasonSave),
        },
        params,
      );
      onSaved(result.cycle, t(m.saved, { name: label.trim() }));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : t(m.failed));
      setBusy(false);
    }
  };

  return (
    <DetailDrawer title={work.label} onClose={onClose} isDirty={editable && changed}>
      <div className="agc-drawer">
        <dl className="agc-facts">
          <Fact label={t(m.factStage)}>{facts.stage}</Fact>
          <Fact label={t(m.factWindow)}>
            <span className="num">{facts.window}</span>
          </Fact>
          <Fact label={t(m.factState)}>
            <Badge tone={work.state === 'launched' ? 'green' : work.state === 'cancelled' ? 'outline' : 'neutral'}>{facts.state}</Badge>
            {facts.tags.length ? <span className="agc-muted"> {facts.tags.join(' · ')}</span> : null}
          </Fact>
          {facts.progress ? <Fact label={t(m.factProgress)}>{facts.progress}</Fact> : null}
        </dl>
        {facts.href ? (
          <Link className="agc-drawer__link" to={facts.href}>
            {t(m.openWork)} <ArrowRight size={14} aria-hidden="true" />
          </Link>
        ) : null}

        {canEdit && isLaunched(work) ? (
          <div className="agc-drawer__block">
            <p className="agc-muted">{t(m.launchedNote)}</p>
            {canReschedule(work) ? (
              <button type="button" className="astra-button" onClick={onReschedule}>
                {t(m.reschedule)}
              </button>
            ) : null}
          </div>
        ) : null}

        {editable ? (
          <div className="agc-form">
            <TextField label={t(m.labelField)} value={label} onChange={setLabel} />
            <RequiredToggle value={required} onChange={setRequired} />
            {byDates ? (
              <DateRangePicker label={t(m.windowField)} value={range} onChange={setRange} minValue={min} maxValue={max} />
            ) : (
              <p className="agc-muted">{t(m.relativeNote)}</p>
            )}
            {error ? (
              <p className="agc-error" role="alert">
                {error}
              </p>
            ) : null}
            <DrawerFormActions
              note={!label.trim() ? t(m.needLabel) : !changed ? t(m.noChanges) : undefined}
              cancel={t(COMMON.cancel)}
              primary={{ label: t(m.save), isDisabled: !changed || !label.trim(), isPending: busy, onPress: () => void save() }}
            />
            <button type="button" className="btn btn--ghost btn--sm agc-form__danger" onClick={onRemove}>
              {t(m.remove)}
            </button>
          </div>
        ) : null}
      </div>
    </DetailDrawer>
  );
}
